<form [formGroup]="territoryForm">
<div class="min-h-[calc(100vh-160px)]">

    <!-- Header -->
    <div class="bg-white border-b border-gray-200 px-6 py-4">
        <div class="flex items-center justify-between">
            <div>
                <h1 class="text-2xl font-bold text-gray-900">{{ isEditMode ? 'Edit Territory' : 'Add New Territory' }}</h1>
                <p class="text-gray-600 mt-1">{{ isEditMode ? 'Modify existing territory details and boundaries' : 'Create a new geographical territory and assign users' }}</p>
            </div>
            <p-button appGlobalLoadingDisable class="hidden md:block" icon="pi pi-arrow-left" label="Back to Territories" severity="secondary" size="small" (onClick)="cancel()">
            </p-button>
        </div>
    </div>

    <!-- Main Content with Flex Layout -->
    <div class="flex flex-col 2xl:flex-row h-[calc(100vh-160px)]">
        <!-- Left Panel - Territory Settings -->
        <div class="order-2 2xl:order-1 2xl:w-96 bg-white border-r border-gray-200 flex flex-col flex-shrink-0 overflow-y-auto">
            <div class="p-6 h-[calc(100vh-160px)]">
                <h2 class="text-lg font-semibold text-gray-900 mb-6">{{ isEditMode ? 'Edit Territory Settings' : 'Territory Settings' }}</h2>

                <div class="space-y-6">
                    <!-- Territory Name -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Territory Name</label>
                        <input pInputText formControlName="name" class="w-full" placeholder="Enter territory name" i18n-placeholder>
                        <small *ngIf="territoryForm.get('name')?.invalid && territoryForm.get('name')?.touched" class="text-red-500">
                            Territory name is required
                        </small>
                    </div>

                    <!-- Region Selection -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2 mt-2 md:mt-0">Select Region</label>
                        <p-select 
                            [options]="regions" 
                            formControlName="region" 
                            optionLabel="name" 
                            placeholder="Search and select region..." i18n-placeholder 
                            styleClass="w-full"
                            [filter]="true"
                            
                            filterBy="name"
                            [showClear]="true">
                        </p-select>
                        <small *ngIf="territoryForm.get('region')?.invalid && territoryForm.get('region')?.touched" class="text-red-500">
                            Region is required
                        </small>
                    </div>

                    <!-- Active Territory Toggle -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-2">Active Territory</label>
                        <p-toggleSwitch formControlName="isActive" styleClass="w-full">
                        </p-toggleSwitch>
                    </div>

                    <!-- Assigned FSRs -->
                    <div>
                        <div class="flex justify-between items-center mb-3">
                            <label class="block text-sm font-medium text-gray-700">Assigned FSRs ({{ selectedUsers.length }})</label>
                            <div *ngIf="availableUsers.length === 0" class="text-xs text-red-500">
                                No users available
                            </div>
                        </div>

                        <!-- Loading State -->
                        <div *ngIf="availableUsers.length === 0 && !loading" class="text-center py-4">
                            <p-progressSpinner size="small"></p-progressSpinner>
                            <p class="text-xs text-gray-500 mt-2">Loading users...</p>
                        </div>

                        <!-- Search FSRs -->
                        <div class="mb-4" *ngIf="availableUsers.length > 0">
                            <p-inputGroup>
                                <p-inputGroupAddon>
                                    <i class="pi pi-search"></i>
                                </p-inputGroupAddon>
                                <input pInputText [(ngModel)]="searchQuery" [ngModelOptions]="{standalone: true}" (ngModelChange)="onSearchChange()" placeholder="Search FSRs..." class="w-full" i18n-placeholder>
                            </p-inputGroup>
                        </div>

                        <!-- Selected FSRs List -->
                        <div class="space-y-2 max-h-48 overflow-y-auto">
                            <div *ngFor="let user of selectedUsers" class="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                <div class="flex items-center">
                                    <p-avatar [label]="user.firstName.charAt(0) + user.lastName.charAt(0)" size="normal" styleClass="bg-blue-100 text-blue-600">
                                    </p-avatar>
                                    <div class="ml-2">
                                        <div class="text-sm font-medium text-gray-700">{{ user.firstName }} {{ user.lastName }}</div>
                                        <div class="text-xs text-gray-500">{{ user.email }}</div>
                                    </div>
                                </div>
                                <p-button appGlobalLoadingDisable icon="pi pi-times" size="small" text severity="danger" (onClick)="removeUserFromTerritory(user._id)">
                                </p-button>
                            </div>
                        </div>

                        <!-- Manage Users Button -->
                        <div class="mt-4">
                            <p-button appGlobalLoadingDisable label="Manage Users" icon="pi pi-users" severity="secondary" size="small" (onClick)="openUserDrawer()" class="w-full mb-2">
                            </p-button>
                        </div>

                        <!-- Available FSRs for adding back -->
                        <div *ngIf="searchQuery && getAvailableUsersForAdding().length > 0" class="mt-4">
                            <label class="block text-sm font-medium text-gray-700 mb-2">Available FSRs</label>
                            <div class="space-y-2 max-h-32 overflow-y-auto">
                                <div *ngFor="let user of getAvailableUsersForAdding()" class="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                                    <div class="flex items-center">
                                        <p-avatar [label]="user.firstName.charAt(0) + user.lastName.charAt(0)" size="normal" styleClass="bg-green-100 text-green-600">
                                        </p-avatar>
                                        <div class="ml-2">
                                            <div class="text-sm font-medium text-gray-700">{{ user.firstName }} {{ user.lastName }}</div>
                                            <div class="text-xs text-gray-500">{{ user.email }}</div>
                                        </div>
                                    </div>
                                    <p-button appGlobalLoadingDisable icon="pi pi-plus" size="small" text severity="success" (onClick)="addUser(user)">
                                    </p-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="flex flex-row gap-2 pt-4 border-t border-gray-200">
                        <p-button [label]="isEditMode ? 'Update Territory' : 'Save Territory'" icon="pi pi-check" severity="primary" [loading]="loading" [disabled]="territoryForm.invalid || currentPolygon.length < 3 || selectedUsers.length === 0 || isPolygonEditingMode" (onClick)="saveTerritory()" class="w-fit">
                        </p-button>
                        <p-button appGlobalLoadingDisable label="Cancel" icon="pi pi-times" severity="secondary" (onClick)="cancel()" class="">
                        </p-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Panel - Map -->
        <div #rightPanel class="order-1 2xl:order-2 right-panel bg-gray-100 ">
            <div class="w-full overflow-scroll">
                <!-- Map Container -->
                <div #mapContainer class="map-container relative">
                    <ng-container *ngIf="shouldRenderMap">
                        <google-map
                            #mapRef
                            [center]="center"
                            [zoom]="zoom"
                            [options]="options"
                            class="min-h-screen transition-opacity duration-200"
                            [class.opacity-0]="mapLoading"
                            [class.pointer-events-none]="mapLoading">

                            <!-- Existing Territory Polygons -->
                            <map-polygon *ngFor="let territory of existingTerritories" [paths]="territory.coordinates" [options]="{
                                fillColor: territory.status === 'Active' ? '#3B82F6' : '#9CA3AF',
                                fillOpacity: 0.3,
                                strokeColor: territory.status === 'Active' ? '#1D4ED8' : '#6B7280',
                                strokeWeight: 2,
                                clickable: true
                            }">
                            </map-polygon>

                            <!-- Current Drawing Polygon (hide during editing mode - the editingOverlay shows the shape instead) -->
                            <map-polygon *ngIf="currentPolygon.length > 0 && !isPolygonEditingMode" [paths]="currentPolygon" [options]="{
                                fillColor: '#4CAF50',
                                fillOpacity: 0.25,
                                strokeColor: '#2E7D32',
                                strokeWeight: 3
                            }">
                            </map-polygon>
                        </google-map>
                    </ng-container>
                    <div *ngIf="mapLoading && !loading" class="absolute inset-0 bg-white flex items-center justify-center z-20">
                        <div class="text-center">
                            <p-progressSpinner></p-progressSpinner>
                            <p class="mt-2 text-gray-600">Loading selected region...</p>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Map Controls Overlay (draggable) -->
            <div *ngIf="mapControlsVisible" #mapControlsCard class="absolute bg-white rounded-lg shadow-lg border border-gray-200 z-10 map-controls" [style.left.px]="mapControlsLeft" [style.top.px]="mapControlsTop">
                <div class="map-controls-drag-handle cursor-grab active:cursor-grabbing select-none py-1.5 px-3 border-b border-gray-100 text-center text-gray-400 hover:text-gray-600" (mousedown)="onMapControlsDragStart($event)">
                    <i class="pi pi-ellipsis-h text-sm"></i>
                </div>
                <div class="p-3">
                    <div class="hidden md:flex flex-col gap-2">
                        <div class="flex flex-row gap-2">
                            <p-button [label]="isDrawingMode ? 'Cancel Drawing' : 'Draw Territory'" [icon]="isDrawingMode ? 'pi pi-times' : 'pi pi-pencil'" [severity]="isDrawingMode ? 'secondary' : 'primary'" size="small" (onClick)="isDrawingMode ? cancelDrawing() : startDrawing()" [disabled]="loading || isEditMode || isPolygonEditingMode" class="w-fit">
                            </p-button>
                            <p-button *ngIf="!isEditMode" label="Clear Polygon" icon="pi pi-trash" severity="secondary" size="small" (onClick)="clearPolygon()" [disabled]="currentPolygon.length === 0 || isPolygonEditingMode" class="w-fit">
                            </p-button>
                            <p-button [label]="isPolygonEditingMode ? 'Stop Editing' : 'Edit Polygon'" [icon]="isPolygonEditingMode ? 'pi pi-stop' : 'pi pi-pencil'" [severity]="isPolygonEditingMode ? 'danger' : 'secondary'" size="small" (onClick)="isPolygonEditingMode ? stopPolygonEditing() : startPolygonEditing()" [disabled]="currentPolygon.length === 0" [class]="'w-fit control-button' + (isPolygonEditingMode ? ' editing' : '')">
                            </p-button>
                            <p-button 
                                [label]="snapToEdge ? 'Magnetic: ON' : 'Magnetic: OFF'" 
                                [icon]="snapToEdge ? 'pi pi-link' : 'pi pi-circle'" 
                                [severity]="snapToEdge ? 'success' : 'secondary'" 
                                size="small" 
                                (onClick)="toggleSnapToEdge()" 
                                class="w-fit">
                            </p-button>
                        </div>
                        <!-- Snap Distance Slider -->
                        <div *ngIf="snapToEdge" class="flex flex-col gap-1 pt-2 border-t border-gray-200">
                            <label class="text-xs text-gray-600">Snap Distance: {{ snapDistanceMeters }}m</label>
                            <input 
                                type="range" 
                                min="10" 
                                max="10000" 
                                step="10" 
                                [(ngModel)]="snapDistanceMeters" 
                                (ngModelChange)="onSnapThresholdChange()" 
                                [ngModelOptions]="{standalone: true}"
                                class="w-full">
                            <div class="flex justify-between text-xs text-gray-500">
                                <span i18n>10m</span>
                                <span i18n>Balance</span>
                                <span i18n>10km</span>
                            </div>
                        </div>
                    </div>
                    <div class="flex md:hidden flex-col gap-2">
                        <div class="flex flex-row gap-2">
                            <p-button [icon]="isDrawingMode ? 'pi pi-times' : 'pi pi-pencil'" [severity]="isDrawingMode ? 'secondary' : 'primary'" size="small" (onClick)="isDrawingMode ? cancelDrawing() : startDrawing()" [disabled]="loading || isEditMode || isPolygonEditingMode" class="w-fit">
                            </p-button>
                            <p-button *ngIf="!isEditMode" icon="pi pi-trash" severity="secondary" size="small" (onClick)="clearPolygon()" [disabled]="currentPolygon.length === 0 || isPolygonEditingMode" class="w-fit">
                            </p-button>
                            <p-button [icon]="isPolygonEditingMode ? 'pi pi-stop' : 'pi pi-wrench'" [severity]="isPolygonEditingMode ? 'danger' : 'secondary'" size="small" (onClick)="isPolygonEditingMode ? stopPolygonEditing() : startPolygonEditing()" [disabled]="currentPolygon.length === 0" [class]="'w-fit control-button' + (isPolygonEditingMode ? ' editing' : '')">
                            </p-button>
                            <p-button 
                                [icon]="snapToEdge ? 'pi pi-link' : 'pi pi-circle'" 
                                [severity]="snapToEdge ? 'success' : 'secondary'" 
                                size="small" 
                                (onClick)="toggleSnapToEdge()" 
                                class="w-fit">
                            </p-button>
                        </div>
                        <!-- Snap Distance Slider - Mobile -->
                        <div *ngIf="snapToEdge" class="flex flex-col gap-1 pt-2 border-t border-gray-200">
                            <label class="text-xs text-gray-600">Snap: {{ snapDistanceMeters }}m</label>
                            <input 
                                type="range" 
                                min="10" 
                                max="10000" 
                                step="10" 
                                [(ngModel)]="snapDistanceMeters" 
                                (ngModelChange)="onSnapThresholdChange()" 
                                [ngModelOptions]="{standalone: true}"
                                class="w-full">
                        </div>
                    </div>
                </div>
            </div>


    <!-- Drawing Instructions -->
    <!-- <div *ngIf="!currentPolygon.length" class="absolute bottom-4 left-4 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-xs z-10">
        <div class="flex items-center">
            <i class="pi pi-info-circle text-blue-600 mr-2"></i>
            <div>
                <h4 class="text-sm font-medium text-blue-800">Drawing Instructions</h4>
                <p class="text-xs text-blue-700 mt-1">Click "Draw Territory" and then click on the map to create polygon points. Double-click to complete.</p>
                <p *ngIf="snapToEdge" class="text-xs text-green-700 mt-2 font-semibold">
                    ✨ Magnetic Mode: Use the slider to adjust snap distance
                </p>
            </div>
        </div>
    </div> -->

    <!-- Territory Drawing Status -->
    <!-- <div *ngIf="currentPolygon.length === 0" class="absolute top-4 right-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-w-xs z-10">
        <div class="flex items-center">
            <i class="pi pi-exclamation-triangle text-yellow-600 mr-2"></i>
            <div>
                <h4 class="text-sm font-medium text-yellow-800">Draw Territory</h4>
                <p class="text-xs text-yellow-700 mt-1">Use the map controls to draw the territory polygon</p>
            </div>
        </div>
    </div> -->

    <!-- <div *ngIf="currentPolygon.length > 0 && !isPolygonEditingMode" class="absolute bottom-4 left-4 bg-green-50 border border-green-200 rounded-lg p-4 max-w-xs z-10">
        <div class="flex items-center">
            <i class="pi pi-check-circle text-green-600 mr-2"></i>
            <div>
                <h4 class="text-sm font-medium text-green-800">Territory Drawn</h4>
                <p class="text-xs text-green-700 mt-1">{{ currentPolygon.length }} coordinates captured</p>
                <p *ngIf="!snapToEdge" class="text-xs text-orange-600 mt-2 italic">
                    💡 Tip: Enable "Magnetic: ON" to snap borders to existing territories
                </p>
            </div>
        </div>
    </div> -->

    <!-- Edit Mode Instructions -->
    <!-- <div *ngIf="isPolygonEditingMode" class="absolute bottom-4 left-4 bg-purple-50 border border-purple-200 rounded-lg p-4 max-w-sm z-10">
        <div class="flex items-center">
            <i class="pi pi-pencil text-purple-600 mr-2"></i>
            <div>
                <h4 class="text-sm font-medium text-purple-800">Edit Mode Active</h4>
                <p class="text-xs text-purple-700 mt-1">
                    <strong>Tips:</strong><br>
                    • Drag vertices to move them<br>
                    • Click an edge to add a vertex<br>
                    • Right-click a vertex to delete it<br>
                    • Hold at least 3 vertices
                </p>
            </div>
        </div>
    </div> -->

        </div>
    </div>

    <!-- User Management Drawer -->
    <p-drawer [(visible)]="showUserDrawer" position="right" [modal]="true" [style]="{width: '400px'}" styleClass="p-drawer-lg user-drawer">

        <ng-template pTemplate="header">
            <div class="flex items-center justify-between w-full">
                <h3 class="text-lg font-semibold text-gray-900">Manage Territory Users</h3>

            </div>
        </ng-template>

        <ng-template pTemplate="content">
            <div class="space-y-6">
                <!-- Search Users -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Search Users</label>
                    <p-inputGroup>
                        <p-inputGroupAddon>
                            <i class="pi pi-search"></i>
                        </p-inputGroupAddon>
                        <input pInputText [(ngModel)]="userSearchQuery" [ngModelOptions]="{standalone: true}" (ngModelChange)="onUserSearchChange()" placeholder="Search by name or email..." i18n-placeholder class="w-full">
                    </p-inputGroup>
                </div>

                <!-- Available Users -->
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Available Users</label>
                    <div class="space-y-2 max-h-64 overflow-y-auto">
                        <div *ngFor="let user of availableUsers" class="flex items-center justify-between p-3 bg-gray-50 rounded-lg user-item">
                            <div class="flex items-center">
                                <p-avatar [label]="user.firstName.charAt(0) + user.lastName.charAt(0)" size="normal" styleClass="bg-blue-100 text-blue-600">
                                </p-avatar>
                                <div class="ml-3">
                                    <div class="text-sm font-medium text-gray-700">{{ user.firstName }} {{ user.lastName }}</div>
                                    <div class="text-xs text-gray-500">{{ user.email }}</div>
                                </div>
                            </div>
                            <p-button *ngIf="!isUserSelected(user._id)" icon="pi pi-plus" size="small" text severity="success" (onClick)="addUserToTerritory(user)" [disabled]="isUserSelected(user._id)">
                            </p-button>
                            <p-button *ngIf="isUserSelected(user._id)" icon="pi pi-times" size="small" text severity="danger" (onClick)="removeUserFromTerritory(user._id)">
                            </p-button>
                        </div>
                    </div>
                </div>

                <!-- Assigned Users Summary -->
                <div class="pt-4 border-t border-gray-200">
                    <div class="flex items-center justify-between">
                        <span class="text-sm font-medium text-gray-700">Total Assigned: {{ selectedUsers.length }}</span>
                        <p-button appGlobalLoadingDisable label="Done" icon="pi pi-check" severity="primary" size="small" (onClick)="closeUserDrawer()">
                        </p-button>
                    </div>
                </div>
            </div>
        </ng-template>
    </p-drawer>

    <!-- Toast Messages -->
    <p-toast></p-toast>


    <!-- Loading Overlay -->
    <div *ngIf="mapLoading" class="absolute inset-0 bg-white  flex items-center justify-center z-20">
        <div class="text-center">
            <p-progressSpinner></p-progressSpinner>
            <p class="mt-2 text-gray-600">Loading...</p>
        </div>
    </div>


</div>

<div class="fixed bottom-4 right-4 bg-white p-4 card rounded-lg shadow-lg hidden md:block">
    <div class="flex flex-row gap-2 ">
        <p-button [label]="isEditMode ? 'Update Territory' : 'Save Territory'" icon="pi pi-check" severity="primary" [loading]="loading" [disabled]="territoryForm.invalid || currentPolygon.length < 3 || selectedUsers.length === 0 || isPolygonEditingMode" (onClick)="saveTerritory()" class="w-fit">
        </p-button>
        <!-- <p-button appGlobalLoadingDisable label="Cancel" icon="pi pi-times" severity="secondary" (onClick)="cancel()" class="">
        </p-button> -->
    </div>
</div>
</form>